import React from 'react';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import { Box } from '@openfin/ui-library';

import { TemplateMarkdown } from '../../../../client/templates/templates';
import { buildFormSchema, getFormDefaultValues } from '../../../model/forms/validation';
import { FieldComponent, WidgetComponentMap } from '../../../model/forms/components';
import { StoredNotification } from '../../../model/StoredNotification';
import { NotificationFormData } from '../../../../client/forms/fields';
import { ResizeContext } from '../Wrappers/ResizeWrapper';
import { ERROR_LABEL_HEIGHT } from '../Forms/ErrorMessageLabel/ErrorMessageLabel';

interface Props {
  notification: StoredNotification<TemplateMarkdown>;
  onSubmit?: (
    notification: StoredNotification<TemplateMarkdown>,
    form: Record<string, unknown>
  ) => void;
}

export const NotificationForm: React.FC<Props> = (props) => {
  const { notification, onSubmit, children } = props;
  const form: NotificationFormData = notification.notification.form ?? [];
  const schema = buildFormSchema(form);
  const initialFormValues = getFormDefaultValues(form);
  const submitLock = React.useRef(false);
  const resizeContext = React.useContext(ResizeContext);

  return (
    <Formik
      key={notification.id}
      initialValues={initialFormValues}
      validationSchema={schema}
      validateOnBlur={true}
      validateOnChange={true}
      validateOnMount={true}
      onSubmit={(values) => {
        // Only allow the form to be submitted once
        if (submitLock.current) {
          return;
        }
        submitLock.current = true;
        onSubmit?.(notification, values);
      }}
    >
      {(formProps) => {
        const { errors, touched } = formProps;
        const errorCount = Object.entries(errors)
          .map(([key]) => touched[key])
          .filter((x) => x).length;
        const hasBeenTouched = Object.values(touched).length > 0;

        React.useLayoutEffect(() => {
          if (resizeContext?.emitModify && hasBeenTouched) {
            resizeContext.emitModify({ x: 0, y: errorCount * ERROR_LABEL_HEIGHT });
          }
        }, [errorCount]);

        return (
          <FormContainer>
            <Form autoComplete="off">
              <Box flexDirection="column" gap="base">
                {form.map((field, i) => {
                  const Component = WidgetComponentMap[field.widget.type] as FieldComponent<
                    typeof field
                  >;
                  if (!Component) {
                    // TODO: What do we do when this happens
                    console.error('Unable to render component', field);
                    return null;
                  }
                  return <Component field={field} key={i} />;
                })}
              </Box>
              {children}
            </Form>
          </FormContainer>
        );
      }}
    </Formik>
  );
};

const FormContainer = styled(Box)`
  width: 100%;
  form {
    width: 100%;
    padding-top: ${({ theme }) => theme.px.base};
  }
`;
